/**
 * Satzspiegel des Schwachstellen-Word-Exports für den Fokusmodus.
 *
 * Geometrie und Typografie kommen aus `wordPageGeometry.json` (aus den
 * DOCX-Templates abgeleitet, Backend-Test `test_word_page_geometry.py`):
 * A4 11906×16838 twips, Ränder 1985/1418/1134/1191, Kopfzeile mit Logo drückt
 * den Textkörper auf 126.13 pt, die 3-Absatz-Fußzeile endet ihn bei 764.5 pt.
 * px@96dpi = pt · 96/72 = twips / 15.
 */

import {
  isBlankRichText,
  ptToPx,
  WORD_FONT_FAMILY,
  wordGeometry,
  type WordFindingLeadInput,
} from './wordLineBreaker';

export { isBlankRichText, ptToPx as wordPtToPx };

/** Kopfdaten des Findings für den Vuln-Header-Platzhalter (Template-Felder). */
export type WordFindingLeadChrome = WordFindingLeadInput;

const geo = wordGeometry('de');
const twipsToPx = (twips: number): number => twips / 15;

/** Nutzbare Textbreite des Schwachstelle-Templates (pgSz − Ränder). */
export const WORD_CONTENT_WIDTH_TWIPS = geo.section.contentWidthTwips;

export type WordImageSize = 'full' | 'large' | 'half' | 'small';

export const WORD_IMAGE_SIZE_DEFAULT: WordImageSize = 'half';

/** Festes Mapping Editor ↔ Word-Export. 100 % = Satzspiegelbreite. */
export const WORD_IMAGE_SIZES: readonly {
  id: WordImageSize;
  label: string;
  shortLabel: string;
  fraction: number;
  hint: string;
}[] = [
  { id: 'full', label: 'Vollbreite', shortLabel: 'Voll', fraction: 1, hint: '100 % der Textbreite' },
  { id: 'large', label: 'Groß', shortLabel: 'Groß', fraction: 2 / 3, hint: '2/3 der Textbreite' },
  { id: 'half', label: 'Halb', shortLabel: 'Halb', fraction: 1 / 2, hint: '1/2 der Textbreite (Standard)' },
  { id: 'small', label: 'Klein', shortLabel: 'Klein', fraction: 1 / 3, hint: '1/3 der Textbreite' },
] as const;

const SIZE_BY_ID = Object.fromEntries(WORD_IMAGE_SIZES.map((s) => [s.id, s])) as Record<
  WordImageSize,
  (typeof WORD_IMAGE_SIZES)[number]
>;

export function parseWordImageSize(value: unknown): WordImageSize {
  if (typeof value === 'string' && value in SIZE_BY_ID) return value as WordImageSize;
  return WORD_IMAGE_SIZE_DEFAULT;
}

export function wordImageWidthFraction(size: WordImageSize = WORD_IMAGE_SIZE_DEFAULT): number {
  return SIZE_BY_ID[parseWordImageSize(size)].fraction;
}

export const WORD_PAGE = {
  contentWidthPx: twipsToPx(WORD_CONTENT_WIDTH_TWIPS),
  /** Textkörper zwischen Kopfzeilen-Unterkante und Fußzeilen-Oberkante (638.37 pt). */
  contentHeightPx: ptToPx(geo.pagePt.contentHeight),
  contentHeightPt: geo.pagePt.contentHeight,
  /** 100 % / Vollbreite — identisch zur Export-Textbreite. */
  imageWidthPx: twipsToPx(WORD_CONTENT_WIDTH_TWIPS),
  bodyFont: WORD_FONT_FAMILY.narrow,
  bodySizePt: geo.body.sizePt,
  /** Arial Narrow 12 pt, einfacher Zeilenabstand: 13.8 pt. */
  bodyLinePt: geo.body.linePt,
  bodyAfterPt: geo.body.spaceAfterPt,
  h1SizePt: geo.headings.h1.sizePt,
  h1LinePt: geo.headings.h1.linePt,
  h2SizePt: geo.headings.h2.sizePt,
  h2LinePt: geo.headings.h2.linePt,
  h3SizePt: geo.headings.h3.sizePt,
  h3LinePt: geo.headings.h3.linePt,
  /** Inline-Code: Consolas 8 pt; ein reiner Code-Absatz ist 9.37 pt hoch. */
  codeFont: WORD_FONT_FAMILY.code,
  codeSizePt: geo.inlineCode.sizePt,
  codeLinePt: geo.inlineCode.linePt,
  /** Console-Style (<pre>): Courier New 9 pt, 1.15 Zeilen = 11.72 pt, Einzug 227 twips. */
  consoleFont: WORD_FONT_FAMILY.mono,
  consoleSizePt: geo.console.sizePt,
  consoleLinePt: geo.console.linePt,
  consoleIndentPx: ptToPx(geo.console.indentPt),
  /** MS-Listenabsatz: w:left=720 twips (hängend 360). */
  listIndentPx: ptToPx(geo.list.indentPt),
  blockquoteIndentPx: ptToPx(geo.blockquoteIndentPt),
  /** Descender unter einer Inline-Grafik (Arial Narrow 12 pt). */
  imageDescentPx: ptToPx(geo.imageDescentPt),
} as const;

export function wordImageWidthPx(size: WordImageSize = WORD_IMAGE_SIZE_DEFAULT): number {
  return WORD_PAGE.contentWidthPx * wordImageWidthFraction(size);
}

/** Word: feste Breite je Preset, Höhe über Aspect (wie python-docx `add_picture(width=…)`). */
export function wordImageSizePx(
  naturalWidth: number,
  naturalHeight: number,
  size: WordImageSize = WORD_IMAGE_SIZE_DEFAULT,
): { width: number; height: number } {
  const width = wordImageWidthPx(size);
  if (!(naturalWidth > 0) || !(naturalHeight > 0) || !(width > 0)) {
    return { width: Math.max(0, width), height: 0 };
  }
  return { width, height: width * (naturalHeight / naturalWidth) };
}

const FLOW_IMAGE_SELECTOR = 'img, .tiptap-image-node';

/**
 * Meldet Layout-/Inhaltsänderungen des Flusses — inkl. später geladener Bilder
 * (deren Word-Höhe steht erst nach dem Laden in `data-word-height-pt`).
 */
export function attachWordFlowObservers(root: HTMLElement, onChange: () => void): () => void {
  const watched = new Set<Element>();
  let ro: ResizeObserver | null = null;

  const watch = (el: Element) => {
    if (watched.has(el)) return;
    watched.add(el);
    ro?.observe(el);
    if (el instanceof HTMLImageElement) {
      el.addEventListener('load', onChange);
      el.addEventListener('error', onChange);
    }
  };

  const scan = () => {
    root.querySelectorAll(FLOW_IMAGE_SELECTOR).forEach(watch);
  };

  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(onChange);
    ro.observe(root);
  }
  scan();

  const mo = new MutationObserver(() => {
    scan();
    onChange();
  });
  mo.observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: [
      'src',
      'style',
      'width',
      'height',
      'data-word-size',
      'data-word-height-pt',
      'data-word-block',
      'data-page-break',
      'data-page-break-before',
      'class',
    ],
  });

  return () => {
    mo.disconnect();
    ro?.disconnect();
    watched.forEach((el) => {
      if (el instanceof HTMLImageElement) {
        el.removeEventListener('load', onChange);
        el.removeEventListener('error', onChange);
      }
    });
    watched.clear();
  };
}
